import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { registerBrandIcons } from './core/icons/brand-icons';
import { NgModule, ErrorHandler, provideZoneChangeDetection, provideAppInitializer, inject } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';
import { BrowserModule } from '@angular/platform-browser';
import { FullscreenOverlayContainer, OverlayContainer } from '@angular/cdk/overlay';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { UserPreferencesService } from './shared/services/user-preferences/user-preferences.service';
import { PermissionService } from './core/services/permission.service';
import { authService } from './shared/services/auth/auth.service';
import { companyContextInitializer, permissionInitializer, sessionInitializer } from './app-initializers';

export { companyContextInitializer, permissionInitializer, sessionInitializer };
import {
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth/auth.interceptor';
import { ImpersonationBannerComponent } from './shared/components/impersonation-banner/impersonation-banner.component';
import { ToastrModule } from 'ngx-toastr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './shared/shared.module';
import { ScheduleModule } from '@syncfusion/ej2-angular-schedule';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { SidePanelStackService } from './shared/side-panel-stack/side-panel-stack.service';
@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    SharedModule,
    // Toasts as glass snackbars (src/app/shared/styles/_toast.scss): bottom centre, a close
    // button, and long enough to read; hovering keeps one open.
    ToastrModule.forRoot({
      timeOut: 5000,
      extendedTimeOut: 2000,
      positionClass: 'toast-bottom-center',
      preventDuplicates: true,
      closeButton: true,
      toastClass: 'ngx-toastr clb-toast',
    }),
    ScheduleModule,
    NgxMatSelectSearchModule,
    // COL-773: shown on every screen while Colobbo support is signed in as someone.
    ImpersonationBannerComponent,
  ],
  providers: [
    // Angular 22 is zoneless-first: without this, an NgModule app falls back to
    // NoopNgZone and async (HTTP/timers) no longer triggers change detection.
    // Replaces the deprecated bootstrapModule({ ngZoneEventCoalescing: true }) option.
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Menus, dialogs, tooltips and toasts are drawn in one overlay layer on <body>. With this, the
    // layer moves into whatever is full screen (the live map's full-screen button), so they still
    // show there; otherwise it behaves exactly as before.
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    provideHttpClient(withInterceptors([authInterceptor])),
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    // Side panels opened from side panels drill in instead of stacking (COL-973).
    provideAppInitializer(() => inject(SidePanelStackService).init()),
    // Brand icons: <mat-icon svgIcon="c:<name>"> (COL-972, redesign checkpoint 2).
    provideAppInitializer(() => registerBrandIcons(inject(MatIconRegistry), inject(DomSanitizer))),
    provideAppInitializer(() =>
      sessionInitializer(inject(UserPreferencesService), inject(PermissionService), inject(authService))()
    ),
    ...(environment.sentryEnabled ? [
      { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => { inject(Sentry.TraceService); }),
    ] : []),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
